import { Alert, Box, CircularProgress } from "@mui/material";
import { type TreeItemProps } from "@mui/x-tree-view";
import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useState,
  type DragEvent,
  type FC,
  type MouseEvent,
} from "react";
import toast from "react-hot-toast";
import { useSearchParams } from "react-router";
import {
  ArchiveFolderModal,
  type ArchiveFolderData,
} from "../../modules/assets/archive-folder-modal";
import { ArchiveConfirmation, AssetPreviewModal } from "../../modules/assets/asset-preview-modal";
import { ContentLibraryAssets } from "../../modules/assets/components/content-library-assets";
import { ContentLibraryFilter } from "../../modules/assets/components/content-library-filter";
import { ContentLibraryHeader } from "../../modules/assets/components/content-library-header";
import { ContentLibrarySidebar } from "../../modules/assets/components/content-library-sidebar";
import { CustomTreeItem } from "../../modules/assets/components/custom-tree-item";
import { FolderBreadcrumb } from "../../modules/assets/components/folder-breadcrumb";
import { useUpdateFolderParentCallback } from "../../modules/assets/components/folder-parent";
import { ContentTypeChips } from "../../modules/assets/content-type-chips";
import { contentTypes as allContentTypes } from "../../modules/assets/content-types";
import { CreateFolderModal, type CreateFolderData } from "../../modules/assets/create-folder-modal";
import { EmptyState } from "../../modules/assets/empty-state";
import { useAssets } from "../../modules/assets/hooks/use-assets";
import { useFolders } from "../../modules/assets/hooks/use-folders";
import { MoveFolderModal, type MoveFolderData } from "../../modules/assets/move-folder-modal";
import {
  contentLibraryReducer,
  filterAssets,
  initialState,
} from "../../modules/assets/reducer/reducer";
import type { Asset } from "../../modules/assets/server/digital-asset-service";
import { SupabaseDigitalAssetService } from "../../modules/assets/server/digital-asset-service";
import type { Folder } from "../../modules/assets/server/folder-service";
import { TagChips } from "../../modules/assets/tag-chips";
import { UploadNewFileModal } from "../../modules/assets/upload-new-file-modal";
import {
  constructBreadcrumbs,
  folderNameFromId,
  folderParentMap,
  getDescendantFolderIds,
  ROOT_ID,
  transformFoldersToTreeItems,
} from "../../modules/assets/utils";
import { useAccount } from "../../modules/generic/hooks/use-account";
import { Page } from "../../modules/layout/components/page";
import { PageContent } from "../../modules/layout/components/page-content";
import { SearchField } from "../../modules/search/search-field";
import { useSupabaseCallback } from "../../server/supabase/hooks";
import { useDialog } from "../../utils/hooks/use-dialog";
import type { UUID } from "../../utils/uuid";

function extractAvailableTags(assets: Asset[]): string[] {
  return Array.from(new Set(assets.flatMap((asset) => asset.tags))).sort();
}

const ASSET_DRAG_TYPE = "application/x-changeengine-asset";
const ASSETS_DRAG_TYPE = "application/x-changeengine-assets";
const FOLDER_DRAG_TYPE = "application/x-changeengine-folder";

const ContentLibraryPage: FC = () => {
  const [state, dispatch] = useReducer(contentLibraryReducer, initialState);
  const [searchFieldKey, setSearchFieldKey] = useState(0);
  const [latestAssets, setLatestAssets] = useState<Asset[] | null>(null);
  const [latestFolders, setLatestFolders] = useState<Folder[] | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const previewModal = useDialog();
  const uploadModal = useDialog();
  const createFolderModal = useDialog<CreateFolderData>();
  const archiveFolderModal = useDialog<ArchiveFolderData>();
  const archiveAssetsModal = useDialog<{ assetIds: UUID[] }>();
  const moveFolderModal = useDialog<MoveFolderData>();
  const { handleOpen: openCreateFolderModal } = createFolderModal;
  const { handleOpen: openArchiveFolderModal } = archiveFolderModal;
  const { handleOpen: openMoveFolderModal } = moveFolderModal;

  const { data, loading, error, reload } = useAssets();
  const {
    data: foldersData,
    loading: foldersLoading,
    error: foldersError,
    reload: reloadFolders,
  } = useFolders();
  const account = useAccount();
  const updateFolderParent = useUpdateFolderParentCallback();

  useEffect(() => {
    if (data) {
      setLatestAssets(data);
    }
  }, [data]);

  useEffect(() => {
    if (foldersData) {
      setLatestFolders(foldersData);
    }
  }, [foldersData]);

  const assets = data ?? latestAssets;
  const folders = foldersData ?? latestFolders;

  const moveAssets = useSupabaseCallback(
    async ({ supabase }, assetIds: UUID[], targetFolderId: UUID | null) => {
      const service = new SupabaseDigitalAssetService(supabase);
      const { error: updateError } = await service.updateFolderForAssets(assetIds, targetFolderId);
      if (updateError) throw updateError;
    },
    [],
  );

  const archiveAssets = useSupabaseCallback(async ({ supabase }, assetIds: UUID[]) => {
    const service = new SupabaseDigitalAssetService(supabase);
    const { error: archiveError } = await service.archiveAssets(assetIds);
    if (archiveError) throw archiveError;
  }, []);

  useEffect(() => {
    const formats = searchParams.has("format") ? searchParams.getAll("format") : [];
    const tags = searchParams.has("tags") ? searchParams.getAll("tags") : [];
    const previewAssetId = searchParams.get("preview");
    const selectedFolderId = searchParams.get("folder") ?? ROOT_ID;

    dispatch({
      type: "SYNC_FROM_URL",
      payload: { contentTypes: formats, tags, previewAssetId, selectedFolderId },
    });
  }, [searchParams]);

  useEffect(() => {
    dispatch({ type: "CLEAR_SELECTION_ON_CONTEXT_CHANGE" });
  }, [state.selectedTreeItemId, state.appliedContentTypes, state.appliedTags, state.searchText]);

  useEffect(() => {
    if (state.previewAssetId && !previewModal.open) {
      previewModal.handleOpen();
    } else if (!state.previewAssetId && previewModal.open) {
      previewModal.handleClose();
    }
  }, [state.previewAssetId, previewModal]);

  const selectedAsset = useMemo(() => {
    if (!state.previewAssetId || !assets) return null;
    return assets.find((x) => x.id === state.previewAssetId) ?? null;
  }, [state.previewAssetId, assets]);

  const treeItems = useMemo(() => {
    if (!folders || foldersError) return [];
    return transformFoldersToTreeItems(folders);
  }, [folders, foldersError]);

  const folderMap = useMemo(() => {
    return folderParentMap(folders);
  }, [folders]);

  useEffect(() => {
    if (!state.selectedTreeItemId || treeItems.length === 0) return;

    const findPath = (
      items: typeof treeItems,
      targetId: string,
      path: string[],
    ): string[] | null => {
      for (const item of items) {
        const currentPath = [...path, item.id];
        if (item.id === targetId) return currentPath;
        if (item.children?.length) {
          const result = findPath(item.children, targetId, currentPath);
          if (result) return result;
        }
      }
      return null;
    };

    const path = findPath(treeItems, state.selectedTreeItemId, []);
    if (!path) return;

    const parentIds = path.slice(0, -1);
    const nextExpanded = Array.from(new Set([...state.expandedItems, ...parentIds]));
    if (nextExpanded.length !== state.expandedItems.length) {
      dispatch({ type: "SET_EXPANDED_ITEMS", payload: nextExpanded });
    }
  }, [state.selectedTreeItemId, state.expandedItems, treeItems]);

  const availableTags = useMemo(() => {
    if (!assets) return [];
    return extractAvailableTags(assets);
  }, [assets]);

  // Compute filtered assets based on current state
  const filteredData = useMemo(() => {
    if (!assets) return null;
    return filterAssets(
      assets,
      {
        selectedTreeItemId: state.selectedTreeItemId,
        appliedContentTypes: state.appliedContentTypes,
        appliedTags: state.appliedTags,
        searchText: state.searchText,
      },
      treeItems,
    );
  }, [
    assets,
    state.selectedTreeItemId,
    state.appliedContentTypes,
    state.appliedTags,
    state.searchText,
    treeItems,
  ]);

  const filteredAllData = useMemo(() => {
    if (!assets) return [];
    if (!state.searchText.trim()) return [];
    return filterAssets(
      assets,
      {
        selectedTreeItemId: null,
        appliedContentTypes: state.appliedContentTypes,
        appliedTags: state.appliedTags,
        searchText: state.searchText,
      },
      treeItems,
    );
  }, [assets, state.appliedContentTypes, state.appliedTags, state.searchText, treeItems]);

  const filteredFolders = useMemo(() => {
    if (!folders) return [];

    const folderId = searchParams.get("folder");

    const naturalCollator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });
    return folders
      .filter((f) => f.parent_folder_id === (folderId === ROOT_ID ? null : folderId))
      .filter((f) => f.name.toLowerCase().includes(state.searchText.trim().toLowerCase()))
      .sort((a, b) => naturalCollator.compare(a.name, b.name));
  }, [folders, searchParams, state.searchText]);

  const filteredAllFolders = useMemo(() => {
    if (!folders) return [];
    const searchLower = state.searchText.trim().toLowerCase();
    if (!searchLower) return [];
    const naturalCollator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });
    return folders
      .filter((f) => f.name.toLowerCase().includes(searchLower))
      .sort((a, b) => naturalCollator.compare(a.name, b.name));
  }, [folders, state.searchText]);

  const breadcrumbs = useMemo(() => {
    return constructBreadcrumbs(treeItems, state.selectedTreeItemId);
  }, [treeItems, state.selectedTreeItemId]);

  // Drag and drop handlers
  const handleAssetDragStart = useCallback(
    (asset: Asset, event: DragEvent): void => {
      const draggedAssetIds =
        state.isSelectionMode && state.selectedAssetIds.includes(asset.id)
          ? state.selectedAssetIds
          : [asset.id];

      event.dataTransfer.setData(ASSET_DRAG_TYPE, asset.id);
      event.dataTransfer.setData(ASSETS_DRAG_TYPE, JSON.stringify(draggedAssetIds));
      event.dataTransfer.effectAllowed = "move";
    },
    [state.isSelectionMode, state.selectedAssetIds],
  );

  const handleFolderDragStart = useCallback((folder: Folder, event: DragEvent): void => {
    event.dataTransfer.setData(FOLDER_DRAG_TYPE, folder.id);
    event.dataTransfer.effectAllowed = "move";
  }, []);

  const handleSidebarFolderDragStart = useCallback((folderId: UUID, event: DragEvent): void => {
    event.dataTransfer.setData(FOLDER_DRAG_TYPE, folderId);
    event.dataTransfer.effectAllowed = "move";
  }, []);

  const handleFolderDragOver = useCallback((event: DragEvent): void => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }, []);

  const handleFolderDrop = useCallback(
    async (targetFolderId: UUID | null, event: DragEvent): Promise<void> => {
      event.preventDefault();
      event.stopPropagation();

      const serializedAssetIds = event.dataTransfer.getData(ASSETS_DRAG_TYPE);
      const singleAssetId = event.dataTransfer.getData(ASSET_DRAG_TYPE);
      const folderId = event.dataTransfer.getData(FOLDER_DRAG_TYPE);
      let draggedAssetIds: UUID[] = [];

      if (serializedAssetIds) {
        try {
          const parsed = JSON.parse(serializedAssetIds) as unknown;
          if (Array.isArray(parsed)) {
            draggedAssetIds = parsed.filter((id): id is UUID => typeof id === "string");
          }
        } catch {
          draggedAssetIds = [];
        }
      }

      if (draggedAssetIds.length === 0 && singleAssetId) {
        draggedAssetIds = [singleAssetId as UUID];
      }

      // Handle asset drop
      if (draggedAssetIds.length > 0 && assets) {
        const assetsToMove = assets.filter((asset) => draggedAssetIds.includes(asset.id));
        const moveableAssetIds = assetsToMove
          .filter(
            (asset) =>
              !(asset.folder_id === targetFolderId || (!asset.folder_id && !targetFolderId)),
          )
          .map((asset) => asset.id);
        if (moveableAssetIds.length === 0) return;

        try {
          await moveAssets(moveableAssetIds, targetFolderId);
          const folderName =
            folderNameFromId(targetFolderId ?? ROOT_ID, folders) ?? "Content Library";
          const moveCount = moveableAssetIds.length;
          toast.success(
            moveCount === 1
              ? `Asset moved to '${folderName}'`
              : `${moveCount} assets moved to '${folderName}'`,
          );
          dispatch({ type: "CLEAR_ASSET_SELECTION" });
          reload();
        } catch (err) {
          console.error("Failed to move assets:", err);
          toast.error(
            moveableAssetIds.length > 1 ? "Failed to move assets" : "Failed to move asset",
          );
        }
        return;
      }

      // Handle folder drop
      if (folderId && folders) {
        const folder = folders.find((f) => f.id === folderId);
        if (!folder) return;

        // Can't drop on itself
        if (folder.id === targetFolderId) return;

        // Can't drop on current parent (no change)
        if (folder.parent_folder_id === targetFolderId) return;
        if (folder.parent_folder_id === null && targetFolderId === null) return;

        // Can't drop on a descendant
        if (targetFolderId) {
          const descendants = getDescendantFolderIds(folder.id, folders);
          if (descendants.has(targetFolderId)) {
            toast.error("Cannot move a folder into its own subfolder");
            return;
          }
        }

        try {
          const result = await updateFolderParent(folder.id, targetFolderId);
          if (result?.success) {
            const folderName =
              folderNameFromId(targetFolderId ?? ROOT_ID, folders) ?? "Content Library";
            toast.success(`Folder moved to '${folderName}'`);
            reloadFolders();
            reload();
          } else {
            throw new Error(result?.error ?? "Unknown error");
          }
        } catch (err) {
          console.error("Failed to move folder:", err);
          if (err instanceof Error && err.message.includes("folder_unique_name")) {
            toast.error("A folder with this name already exists in the destination");
          } else {
            toast.error("Failed to move folder");
          }
        }
      }
    },
    [assets, folders, moveAssets, updateFolderParent, reload, reloadFolders],
  );

  const handleFolderSelect = useCallback(
    (folderId: string): void => {
      dispatch({ type: "SELECT_FOLDER", payload: folderId });

      const params = new URLSearchParams(searchParams);
      if (folderId === ROOT_ID) {
        params.delete("folder");
      } else {
        params.set("folder", folderId);
      }
      setSearchParams(params);
    },
    [searchParams, setSearchParams],
  );

  const openAssetPreview = useCallback(
    (asset: Asset) => {
      dispatch({ type: "SET_PREVIEW_ASSET_ID", payload: asset.id });
      const params = new URLSearchParams(searchParams);
      params.set("preview", asset.id);
      setSearchParams(params);
    },
    [searchParams, setSearchParams],
  );

  const handleSelectAsset = useCallback(
    (asset: Asset, event: MouseEvent, visibleAssets: Asset[]) => {
      if (state.isSelectionMode) {
        const hasAdditiveModifier = event.metaKey || event.ctrlKey;
        if (event.shiftKey && state.selectionAnchorAssetId) {
          const anchorIndex = visibleAssets.findIndex((a) => a.id === state.selectionAnchorAssetId);
          const currentIndex = visibleAssets.findIndex((a) => a.id === asset.id);
          if (anchorIndex !== -1 && currentIndex !== -1) {
            const start = Math.min(anchorIndex, currentIndex);
            const end = Math.max(anchorIndex, currentIndex);
            const rangeIds = visibleAssets.slice(start, end + 1).map((a) => a.id);
            dispatch({
              type: "SELECT_ASSET_RANGE",
              payload: { assetIds: rangeIds, append: hasAdditiveModifier },
            });
            dispatch({ type: "SET_SELECTION_ANCHOR", payload: asset.id });
            return;
          }
        }

        dispatch({ type: "TOGGLE_ASSET_SELECTION", payload: asset.id });
        dispatch({ type: "SET_SELECTION_ANCHOR", payload: asset.id });
        return;
      }

      openAssetPreview(asset);
    },
    [openAssetPreview, state.isSelectionMode, state.selectionAnchorAssetId],
  );

  const handleClosePreviewModal = useCallback(() => {
    dispatch({ type: "SET_PREVIEW_ASSET_ID", payload: null });
    const params = new URLSearchParams(searchParams);
    params.delete("preview");
    setSearchParams(params);
  }, [searchParams, setSearchParams]);

  const handleArchived = useCallback(() => {
    handleClosePreviewModal();
    reload();
  }, [handleClosePreviewModal, reload]);

  const handleRemoveContentTypeFilter = useCallback(
    (contentType: string) => {
      dispatch({ type: "REMOVE_CONTENT_TYPE_FILTER", payload: contentType });

      const params = new URLSearchParams(searchParams);
      const newValues = params.getAll("format").filter((c) => c !== contentType);
      params.delete("format");
      newValues.forEach((val) => params.append("format", val));
      setSearchParams(params);
    },
    [searchParams, setSearchParams],
  );

  const handleRemoveTagFilter = useCallback(
    (tag: string) => {
      dispatch({ type: "REMOVE_TAG_FILTER", payload: tag });

      const params = new URLSearchParams(searchParams);
      const newValues = params.getAll("tags").filter((tagValue) => tagValue !== tag);
      params.delete("tags");
      newValues.forEach((val) => params.append("tags", val));
      setSearchParams(params);
    },
    [searchParams, setSearchParams],
  );

  const handleApplyFilter = useCallback(() => {
    dispatch({ type: "APPLY_FILTERS" });

    const params = new URLSearchParams();
    state.selectedContentTypes.forEach((c) => params.append("format", c));
    state.selectedTags.forEach((tag) => params.append("tags", tag));
    if (state.selectedTreeItemId && state.selectedTreeItemId !== ROOT_ID) {
      params.set("folder", state.selectedTreeItemId);
    }
    setSearchParams(params);
  }, [state.selectedContentTypes, state.selectedTags, state.selectedTreeItemId, setSearchParams]);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch({ type: "SET_SEARCH_TEXT", payload: e.target.value.trim() });
  }, []);

  const handleToggleSelectionMode = useCallback(() => {
    if (!state.isSelectionMode) {
      handleClosePreviewModal();
    }
    dispatch({ type: "TOGGLE_SELECTION_MODE" });
  }, [state.isSelectionMode, handleClosePreviewModal]);

  const handleClearSelection = useCallback(() => {
    dispatch({ type: "CLEAR_ASSET_SELECTION" });
  }, []);

  const handleOpenArchiveSelectedAssets = useCallback(() => {
    if (state.selectedAssetIds.length === 0) return;
    archiveAssetsModal.handleOpen({ assetIds: state.selectedAssetIds });
  }, [archiveAssetsModal, state.selectedAssetIds]);

  const handleConfirmArchiveSelectedAssets = useCallback(async () => {
    const selectedIds = archiveAssetsModal.data?.assetIds ?? [];
    if (selectedIds.length === 0) {
      archiveAssetsModal.handleClose();
      return;
    }

    try {
      await archiveAssets(selectedIds);
      const archivedCount = selectedIds.length;
      toast.success(archivedCount === 1 ? "1 asset archived" : `${archivedCount} assets archived`);
      dispatch({ type: "CLEAR_ASSET_SELECTION" });
      archiveAssetsModal.handleClose();
      reload();
    } catch (err) {
      console.error("Failed to archive assets:", err);
      toast.error(selectedIds.length > 1 ? "Failed to archive assets" : "Failed to archive asset");
    }
  }, [archiveAssets, archiveAssetsModal, reload]);

  const handleSidebarToggle = useCallback((open: boolean) => {
    dispatch({ type: "SET_SIDEBAR_OPEN", payload: open });
  }, []);

  const handleExpandedItemsChange = useCallback(
    (_event: React.SyntheticEvent | null, itemIds: string[]) => {
      dispatch({ type: "SET_EXPANDED_ITEMS", payload: itemIds });
    },
    [],
  );

  const isFolderInArchivedPath = useCallback(
    (selectedId: string, archivedId: UUID): boolean => {
      if (!selectedId || selectedId === ROOT_ID) return false;
      let current: string | null = selectedId;
      while (current && current !== ROOT_ID) {
        if (current === archivedId) return true;
        current = folderMap.get(current as UUID) ?? null;
      }
      return false;
    },
    [folderMap],
  );

  const handleSetSelectedContentTypes = useCallback(
    (contentTypes: string[] | ((prev: string[]) => string[])) => {
      if (typeof contentTypes === "function") {
        // For functional updates, we'd need current state - but since ContentLibraryFilter
        // only passes arrays directly, we can handle this case if needed
        return;
      }
      dispatch({ type: "SET_SELECTED_CONTENT_TYPES", payload: contentTypes });
    },
    [],
  );

  const handleSetSelectedTags = useCallback((tags: string[] | ((prev: string[]) => string[])) => {
    if (typeof tags === "function") {
      return;
    }
    dispatch({ type: "SET_SELECTED_TAGS", payload: tags });
  }, []);

  const handleCreateSubFolder = useCallback(
    (parentFolderId: UUID | null) => {
      openCreateFolderModal({ parentFolderId });
    },
    [openCreateFolderModal],
  );

  const handleArchive = useCallback(
    (folderId: UUID | null) => {
      if (!folderId || !folders) {
        return;
      }
      openArchiveFolderModal({
        folderId,
        name: folderNameFromId(folderId, folders) ?? "",
      });
    },
    [folders, openArchiveFolderModal],
  );

  const handleMove = useCallback(
    (folderId: UUID) => {
      if (!folderId || !folders) {
        return;
      }
      openMoveFolderModal({
        folderId,
        name: folderNameFromId(folderId, folders) ?? "",
      });
    },
    [folders, openMoveFolderModal],
  );

  const CustomTreeItemWithReload = useCallback(
    (props: TreeItemProps) => (
      <CustomTreeItem
        {...props}
        reloadFolders={reloadFolders}
        onCreateSubFolder={handleCreateSubFolder}
        onArchive={handleArchive}
        onMove={handleMove}
        onItemDragStart={handleSidebarFolderDragStart}
        onItemDragOver={handleFolderDragOver}
        onItemDrop={(folderId, event) => void handleFolderDrop(folderId, event)}
      />
    ),
    [
      reloadFolders,
      handleCreateSubFolder,
      handleArchive,
      handleMove,
      handleSidebarFolderDragStart,
      handleFolderDragOver,
      handleFolderDrop,
    ],
  );

  const selectedParentFolderId =
    state.selectedTreeItemId && state.selectedTreeItemId !== ROOT_ID
      ? (state.selectedTreeItemId as UUID)
      : null;

  const handleOnClickFolder = useCallback(
    (folderId: string): void => {
      handleFolderSelect(folderId);
      setSearchFieldKey((prev) => prev + 1);
      dispatch({ type: "SET_SEARCH_TEXT", payload: "" });
    },
    [handleFolderSelect],
  );

  return (
    <>
      <Page
        title="Content Library | ChangeEngine"
        sx={{ height: "100%", display: "flex", flexDirection: "column" }}
      >
        <ContentLibraryHeader
          onUpload={() => uploadModal.handleOpen({ parentFolderId: state.selectedTreeItemId })}
          isSelectionMode={state.isSelectionMode}
          selectedAssetCount={state.selectedAssetIds.length}
          onToggleSelectionMode={handleToggleSelectionMode}
          onClearSelection={handleClearSelection}
        />
        <Box sx={{ display: "flex", flex: 1, minHeight: 0 }}>
          <ContentLibrarySidebar
            loading={foldersLoading}
            sidebarOpen={state.sidebarOpen}
            treeItems={treeItems}
            selectedTreeItemId={state.selectedTreeItemId}
            expandedItems={state.expandedItems}
            onToggle={handleSidebarToggle}
            onItemClick={handleOnClickFolder}
            onExpandedItemsChange={handleExpandedItemsChange}
            onCreateSubFolder={() =>
              createFolderModal.handleOpen({ parentFolderId: selectedParentFolderId })
            }
            itemSlot={CustomTreeItemWithReload}
          />
          <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
            <PageContent maxWidth="xl">
              {(loading || foldersLoading) && <CircularProgress />}
              {error && (
                <Alert severity="error">
                  Sorry, there was a problem retrieving your content. Please try again later
                </Alert>
              )}
              {filteredData && filteredFolders && (
                <ContentLibraryAssets
                  allFolders={folders ?? undefined}
                  filteredData={filteredData}
                  allFilteredData={filteredAllData}
                  filteredFolders={filteredFolders}
                  allFilteredFolders={filteredAllFolders}
                  onClickFolder={handleOnClickFolder}
                  state={state}
                  slots={{
                    breadcrumb: (
                      <FolderBreadcrumb
                        breadcrumbs={breadcrumbs}
                        onFolderSelect={handleOnClickFolder}
                      />
                    ),
                    searchField: (
                      <SearchField
                        key={searchFieldKey}
                        label="Search"
                        defaultValue={state.searchText}
                        onChange={handleSearchChange}
                        fullWidth
                      />
                    ),
                    filter: (
                      <ContentLibraryFilter
                        handleApplyFilter={handleApplyFilter}
                        selectedContentTypes={state.selectedContentTypes}
                        setSelectedContentTypes={handleSetSelectedContentTypes}
                        contentTypes={allContentTypes}
                        selectedTags={state.selectedTags}
                        setSelectedTags={handleSetSelectedTags}
                        availableTags={availableTags}
                      />
                    ),
                    contentTypeChips: (
                      <ContentTypeChips
                        onDelete={handleRemoveContentTypeFilter}
                        contentTypes={state.appliedContentTypes}
                      />
                    ),
                    tagChips: (
                      <TagChips onDelete={handleRemoveTagFilter} tags={state.appliedTags} />
                    ),
                  }}
                  designer={account.data?.designer}
                  onSelectAsset={handleSelectAsset}
                  onClearSelection={handleClearSelection}
                  onArchiveSelectedAssets={handleOpenArchiveSelectedAssets}
                  onCreateFolder={() =>
                    createFolderModal.handleOpen({ parentFolderId: selectedParentFolderId })
                  }
                  onArchiveFolder={() => {
                    if (
                      !folders ||
                      !state.selectedTreeItemId ||
                      state.selectedTreeItemId === ROOT_ID
                    ) {
                      return;
                    }
                    archiveFolderModal.handleOpen({
                      name: folderNameFromId(state.selectedTreeItemId as UUID, folders) ?? "",
                      folderId: state.selectedTreeItemId as UUID,
                    });
                  }}
                  onMoveFolder={() => {
                    if (
                      !folders ||
                      !state.selectedTreeItemId ||
                      state.selectedTreeItemId === ROOT_ID
                    ) {
                      return;
                    }
                    moveFolderModal.handleOpen({
                      name: folderNameFromId(state.selectedTreeItemId as UUID, folders) ?? "",
                      folderId: state.selectedTreeItemId as UUID,
                    });
                  }}
                  onAssetDragStart={handleAssetDragStart}
                  onFolderDragStart={handleFolderDragStart}
                  onFolderDragOver={handleFolderDragOver}
                  onFolderDrop={(folderId, event) => void handleFolderDrop(folderId, event)}
                />
              )}
              {assets && assets.length === 0 && <EmptyState />}
            </PageContent>
          </Box>
        </Box>
      </Page>
      <AssetPreviewModal
        asset={selectedAsset}
        open={previewModal.open}
        onClose={handleClosePreviewModal}
        onChange={reload}
        onArchived={handleArchived}
        onViewFileClick={(asset) => {
          openAssetPreview(asset);
        }}
        onFolderChange={reload}
        folders_feature_flag={true}
      />
      <UploadNewFileModal
        key={`upload-modal-${uploadModal.open ? "open" : "closed"}`}
        folders_feature_flag={true}
        open={uploadModal.open}
        onOpen={uploadModal.handleOpen}
        onClose={() => {
          uploadModal.handleClose();
          reloadFolders();
        }}
        onCancel={() => {
          uploadModal.handleClose();
          reloadFolders();
        }}
        onChange={(folderId) => {
          reload();
          const nextFolderId = folderId ?? state.selectedTreeItemId;
          if (nextFolderId) {
            handleFolderSelect(nextFolderId);
          }
        }}
        onViewFileClick={(asset) => {
          openAssetPreview(asset);
        }}
        folderId={
          state.selectedTreeItemId && state.selectedTreeItemId !== ROOT_ID
            ? (state.selectedTreeItemId as UUID)
            : null
        }
      />
      <CreateFolderModal
        key={`create-folder-modal-${createFolderModal.open ? "open" : "closed"}`}
        open={createFolderModal.open}
        onClose={createFolderModal.handleClose}
        reloadFolders={reloadFolders}
        parentFolderId={createFolderModal.data?.parentFolderId ?? null}
      />
      {archiveFolderModal.data && (
        <ArchiveFolderModal
          open={archiveFolderModal.open}
          onClose={() => {
            archiveFolderModal.handleClose();
            if (
              archiveFolderModal.data?.folderId &&
              state.selectedTreeItemId &&
              isFolderInArchivedPath(state.selectedTreeItemId, archiveFolderModal.data.folderId)
            ) {
              const params = new URLSearchParams(searchParams);
              params.delete("folder");
              setSearchParams(params);
            }
          }}
          reloadFolders={reloadFolders}
          name={archiveFolderModal.data.name}
          folderId={archiveFolderModal.data.folderId}
        />
      )}
      {moveFolderModal.data && (
        <MoveFolderModal
          open={moveFolderModal.open}
          onClose={moveFolderModal.handleClose}
          reloadFolders={reloadFolders}
          name={moveFolderModal.data.name}
          folderId={moveFolderModal.data.folderId}
          folders={folders ?? null}
          loading={foldersLoading}
          error={foldersError ?? null}
        />
      )}
      <ArchiveConfirmation
        open={archiveAssetsModal.open}
        assetCount={archiveAssetsModal.data?.assetIds.length ?? 0}
        onClose={archiveAssetsModal.handleClose}
        onConfirm={() => void handleConfirmArchiveSelectedAssets()}
      />
    </>
  );
};

export default ContentLibraryPage;
